import { useTranslation } from "react-i18next"

import type { Library } from "@/api/admin-api"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { getLibraryHostname } from "./library-auth-utils"

const NONE_VALUE = "__none__"

type LibraryCopyAuthSelectProps = {
  id?: string
  /** Selected source library id, or undefined for manual entry. */
  value?: string
  /** Pre-filtered/sorted via getCopyAuthCandidates(). */
  candidates: Library[]
  /** Hostname of the library being configured; matching candidates get a "same host" hint. */
  currentHostname?: string
  /** `null` when the admin switches back to manual entry. */
  onChange: (library: Library | null) => void
}

/** "Copy authentication from existing library" picker shared by the add and edit dialogs. */
export function LibraryCopyAuthSelect({
  id = "copy_auth_library",
  value,
  candidates,
  currentHostname,
  onChange,
}: LibraryCopyAuthSelectProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {t("adminDashboard.libraries.copyAuth.from", "Copy authentication from existing library")}
      </Label>
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(next) => {
          if (next === NONE_VALUE) {
            onChange(null)
            return
          }
          const lib = candidates.find((l) => l.id === next)
          if (lib) onChange(lib)
        }}
      >
        <SelectTrigger className="w-full" id={id}>
          <SelectValue
            placeholder={t(
              "adminDashboard.libraries.copyAuth.placeholder",
              "Select a library or enter credentials manually"
            )}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>
            {t("adminDashboard.libraries.copyAuth.none", "None (manual entry)")}
          </SelectItem>
          {candidates.map((lib) => {
            const host = getLibraryHostname(lib) ?? ""
            const sameHost = Boolean(host && currentHostname && host === currentHostname)
            return (
              <SelectItem key={lib.id} value={lib.id}>
                {lib.display_name || lib.id}
                {host ? ` — ${host}` : ""}
                {sameHost
                  ? ` (${t("adminDashboard.libraries.copyAuth.sameHost", "same host")})`
                  : ""}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.libraries.copyAuth.help",
          "Reuses the stored app registration and Key Vault secret from that library. You can still edit any field below."
        )}
      </p>
    </div>
  )
}
